import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, ShieldSlash, Users, Hourglass, BellRinging, EnvelopeSimple, Check, X, Crown, Heart } from "@phosphor-icons/react";
import { useAuth } from "../lib/AuthContext";
import { api } from "../lib/api";
import { isPushSupported, getPushEnabled, subscribeToPush, unsubscribeFromPush } from "../lib/pushClient";
import AdminIncidentsCard from "../components/AdminIncidentsCard";
import SponsorsAdmin from "../components/admin/SponsorsAdmin";
import AnalyticsCard from "../components/admin/AnalyticsCard";
import BlastHistoryCard from "../components/admin/BlastHistoryCard";
import LightningHistoryCard from "../components/admin/LightningHistoryCard";
import WeeklyEmailCard from "../components/admin/WeeklyEmailCard";

function MemberStat({ label, value, accent }) {
  return (
    <div className="px-4 py-2.5">
      <div className="text-[10px] uppercase tracking-[0.15em] font-bold text-tac-faint">{label}</div>
      <div className={`text-xl font-black font-display tabular ${accent && value > 0 ? "text-tac-primary" : "text-tac-text"}`}>
        {value}
      </div>
    </div>
  );
}

export default function AdminPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(null);
  const [filter, setFilter] = useState("pending"); // 'pending' | 'all'
  const [stats, setStats] = useState(null);
  const [donateStats, setDonateStats] = useState(null);

  useEffect(() => {
    if (!user || user === false || user.role !== "admin") return;
    api.get("/auth/admin/stats").then(({ data }) => setStats(data)).catch(() => {});
    api.get("/metrics/donate-clicks").then(({ data }) => setDonateStats(data)).catch(() => {});
  }, [user]);

  useEffect(() => {
    if (user === null) return;
    if (user === false) {
      navigate("/login?next=/admin", { replace: true });
      return;
    }
    if (user.role !== "admin") {
      navigate("/", { replace: true });
      return;
    }
    (async () => {
      try {
        const { data } = await api.get("/auth/admin/users", {
          params: filter === "pending" ? { pending: true } : {},
        });
        setUsers(data.users);
      } catch (e) {
        setError("Could not load users.");
      }
    })();
  }, [user, navigate, filter]);

  const setVerified = async (u, verified) => {
    setBusy(u.id);
    try {
      const path = verified ? `/auth/admin/users/${u.id}/verify` : `/auth/admin/users/${u.id}/unverify`;
      await api.post(path);
      // If on the pending tab, drop the user from the list since they're no longer pending.
      // On the all tab, just flip their flag.
      if (filter === "pending" && verified) {
        setUsers((prev) => prev.filter((x) => x.id !== u.id));
      } else {
        setUsers((prev) => prev.map((x) => (x.id === u.id ? { ...x, verified } : x)));
      }
    } catch {
      setError("Action failed. Try again.");
    } finally {
      setBusy(null);
    }
  };

  const setRole = async (u, makeAdmin) => {
    if (makeAdmin && !window.confirm(`Make ${u.display_name || u.email} an admin? They get full admin console access.`)) return;
    if (!makeAdmin && !window.confirm(`Remove admin role from ${u.display_name || u.email}?`)) return;
    setBusy(u.id);
    try {
      const path = `/auth/admin/users/${u.id}/${makeAdmin ? "promote" : "demote"}`;
      await api.post(path);
      setUsers((prev) =>
        prev.map((x) =>
          x.id === u.id
            ? { ...x, role: makeAdmin ? "admin" : "user", verified: makeAdmin ? true : x.verified }
            : x
        )
      );
    } catch {
      setError("Action failed. Try again.");
    } finally {
      setBusy(null);
    }
  };

  if (!users && !error) return <div className="p-8 text-[15px] text-tac-dim text-center">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8 space-y-5">
      <div>
        <h1 className="font-display font-black text-2xl text-tac-text flex items-center gap-2">
          <Users size={20} weight="duotone" className="text-tac-primary" /> Admin console
        </h1>
        <p className="text-[15px] text-tac-dim mt-1">
          Promote trusted reporters, manage the statewide alert pipeline, and send daily digests.
        </p>
      </div>

      {stats && (
        <div
          className="grid grid-cols-2 sm:grid-cols-4 border border-tac-border bg-tac-surface/40 divide-x divide-tac-border/60"
          data-testid="admin-member-stats"
        >
          <MemberStat label="Members" value={stats.total} />
          <MemberStat label="Verified" value={stats.verified} />
          <MemberStat label="Admins" value={stats.admins} />
          <MemberStat label="New (7d)" value={stats.new_this_week} accent />
        </div>
      )}

      {donateStats?.sources && (
        <div
          className="border border-tac-border bg-tac-surface/40"
          data-testid="admin-donate-stats"
        >
          <div className="px-4 pt-3 pb-1 flex items-center gap-1.5">
            <Heart size={13} weight="fill" className="text-tac-primary" />
            <span className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-text">
              Donate Clicks by Placement
            </span>
          </div>
          <div className="grid grid-cols-3 divide-x divide-tac-border/60">
            {["incident", "about", "footer"].map((s) => (
              <div key={s} className="px-4 py-3" data-testid={`admin-donate-${s}`}>
                <div className="text-[10px] uppercase tracking-wider text-tac-faint">{s} pages</div>
                <div className="text-xl font-black font-mono-tac text-tac-text">
                  {donateStats.sources[s]?.total ?? 0}
                </div>
                <div className="text-[11px] font-mono-tac text-tac-dim">
                  {donateStats.sources[s]?.last_7d ?? 0} in 7d
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <AnalyticsCard />

      <BlastHistoryCard />

      <LightningHistoryCard />

      <WeeklyEmailCard />

      <AdminAlertsCard />

      <AdminIncidentsCard />

      <SponsorsAdmin />

      <div className="flex items-center gap-2" data-testid="admin-filter-tabs">
        <FilterTab
          active={filter === "pending"}
          onClick={() => { setFilter("pending"); setUsers(null); }}
          icon={<Hourglass size={12} weight="fill" />}
          label="Pending verification"
          count={filter === "pending" && users ? users.length : null}
          testid="admin-tab-pending"
        />
        <FilterTab
          active={filter === "all"}
          onClick={() => { setFilter("all"); setUsers(null); }}
          icon={<Users size={12} weight="bold" />}
          label="All users"
          count={null}
          testid="admin-tab-all"
        />
      </div>

      {filter === "pending" && (
        <div className="text-[14px] text-tac-faint leading-relaxed">
          Users who have applied to be Verified Reporters. Their application
          details are below — review and click <strong className="text-tac-primary">Verify</strong> to grant them the gold shield.
        </div>
      )}

      {error && (
        <div className="text-xs text-tac-danger border border-tac-danger/40 bg-tac-danger/10 rounded-sm px-3 py-2">
          {error}
        </div>
      )}

      <div className="bg-tac-surface/40 border border-tac-border rounded-sm divide-y divide-tac-border/60" data-testid="admin-users-list">
        {(users || []).length === 0 ? (
          <div className="p-6 text-[15px] text-tac-dim text-center">
            {filter === "pending" ? "Nobody waiting — great job staying on top of it." : "No users yet."}
          </div>
        ) : (
          users.map((u) => (
            <div key={u.id} className="px-4 py-3 flex items-center justify-between gap-3" data-testid={`admin-user-${u.id}`}>
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-9 h-9 rounded-full bg-tac-bg border border-tac-border text-tac-dim flex items-center justify-center font-bold shrink-0">
                  {(u.display_name || "?").charAt(0).toUpperCase()}
                </div>
                <div className="min-w-0">
                  <div className="font-bold text-sm text-tac-text truncate flex items-center gap-1.5">
                    {u.display_name}
                    {u.email_verified ? null : (
                      <span title="Email not confirmed" className="text-[9px] uppercase tracking-wider px-1 py-0.5 border border-tac-warning/55 bg-tac-warning/10 text-tac-warning rounded-sm">
                        Unconfirmed
                      </span>
                    )}
                  </div>
                  <div className="text-[14px] text-tac-dim truncate">{u.email}</div>
                  <div className="text-[10px] uppercase tracking-wider text-tac-faint mt-0.5">{u.role}</div>
                  {u.reporter_application && (
                    <details className="mt-1.5" data-testid={`admin-application-${u.id}`}>
                      <summary className="cursor-pointer text-[11px] font-bold uppercase tracking-wider text-tac-primary hover:text-tac-text">
                        View application ▸
                      </summary>
                      <div className="mt-2 text-[12px] text-tac-text space-y-1 bg-tac-bg/60 border border-tac-border rounded-sm px-3 py-2">
                        <ApplicationRow label="Real name" value={u.reporter_application.real_name} />
                        <ApplicationRow label="Location" value={u.reporter_application.location} />
                        {u.reporter_application.affiliation && (
                          <ApplicationRow label="Affiliation" value={u.reporter_application.affiliation} />
                        )}
                        <ApplicationRow label="Qualifications" value={u.reporter_application.qualifications} multiline />
                        {u.reporter_application.references && (
                          <ApplicationRow label="References" value={u.reporter_application.references} />
                        )}
                        {u.reporter_application.applied_at && (
                          <ApplicationRow
                            label="Submitted"
                            value={new Date(u.reporter_application.applied_at).toLocaleString()}
                          />
                        )}
                      </div>
                    </details>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                {u.role === "admin" && (
                  <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-tac-warning/55 bg-tac-warning/10 text-tac-warning rounded-sm">
                    <Crown size={9} weight="fill" /> Admin
                  </span>
                )}
                {u.verified ? (
                  <>
                    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider border border-tac-primary/55 bg-tac-primary/15 text-tac-primary rounded-sm">
                      <ShieldCheck size={9} weight="fill" /> Verified
                    </span>
                    {u.role !== "admin" && (
                      <button
                        data-testid={`admin-unverify-${u.id}`}
                        onClick={() => setVerified(u, false)}
                        disabled={busy === u.id}
                        className="text-[15px] font-bold uppercase tracking-wider px-2 py-1 border border-tac-border bg-tac-surface hover:bg-tac-surface2 text-tac-dim rounded-sm disabled:opacity-40"
                        title="Remove verified status"
                      >
                        <ShieldSlash size={11} weight="bold" />
                      </button>
                    )}
                  </>
                ) : (
                  <button
                    data-testid={`admin-verify-${u.id}`}
                    onClick={() => setVerified(u, true)}
                    disabled={busy === u.id}
                    className="text-[10px] font-bold uppercase tracking-wider px-3 py-1.5 bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-40 text-white rounded-sm flex items-center gap-1"
                  >
                    <ShieldCheck size={11} weight="fill" /> Verify
                  </button>
                )}
                {u.role !== "admin" ? (
                  <button
                    data-testid={`admin-promote-${u.id}`}
                    onClick={() => setRole(u, true)}
                    disabled={busy === u.id}
                    className="text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 border border-tac-warning/55 text-tac-warning hover:bg-tac-warning/10 rounded-sm flex items-center gap-1 disabled:opacity-40"
                    title="Promote to admin"
                  >
                    <Crown size={11} weight="bold" /> Make admin
                  </button>
                ) : (
                  u.id !== user?.id && (
                    <button
                      data-testid={`admin-demote-${u.id}`}
                      onClick={() => setRole(u, false)}
                      disabled={busy === u.id}
                      className="text-[10px] font-bold uppercase tracking-wider px-2 py-1.5 border border-tac-border text-tac-dim hover:bg-tac-surface2 rounded-sm flex items-center gap-1 disabled:opacity-40"
                      title="Remove admin role"
                    >
                      <X size={11} weight="bold" /> Remove admin
                    </button>
                  )
                )}
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function AdminAlertsCard() {
  const [pushBusy, setPushBusy] = useState(false);
  const [pushMsg, setPushMsg] = useState(null);
  const [digestBusy, setDigestBusy] = useState(false);
  const [digestMsg, setDigestMsg] = useState(null);
  const [diag, setDiag] = useState(null);
  const supported = isPushSupported();
  const pushEnabled = supported && getPushEnabled();

  const enableAdminPush = async () => {
    setPushBusy(true);
    setPushMsg(null);
    try {
      // Subscribe (will reuse existing subscription if present)
      const { client_id } = await subscribeToPush([]);
      // Flag the subscription as admin so it gets ALL statewide wildfires
      await api.post("/push/admin-mode", { client_id, enabled: true });
      setPushMsg({ tone: "ok", text: "Statewide admin alerts enabled. Every new WA wildfire will buzz this device." });
    } catch (e) {
      setPushMsg({ tone: "err", text: e?.response?.data?.detail || e?.message || "Could not enable." });
    } finally {
      setPushBusy(false);
    }
  };

  const sendAdminTestPush = async () => {
    setPushBusy(true);
    setPushMsg(null);
    try {
      const { client_id } = await subscribeToPush([]);
      await api.post("/push/admin-mode", { client_id, enabled: true });
      await api.post("/push/admin-test", { client_id });
      setPushMsg({ tone: "ok", text: "Test push sent — check your device in ~3 seconds." });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg =
        detail ||
        (e?.code === "ECONNABORTED" || e?.message === "Network Error"
          ? "Push gateway didn't respond in time. Click Reset, then re-enable and try again."
          : e?.message) ||
        "Test failed.";
      setPushMsg({ tone: "err", text: msg });
    } finally {
      setPushBusy(false);
    }
  };

  const resetPush = async () => {
    setPushBusy(true);
    setPushMsg(null);
    setDiag(null);
    try {
      await unsubscribeFromPush();
      setPushMsg({
        tone: "ok",
        text: "Push reset complete. Click \"Enable on this device\" to re-subscribe.",
      });
    } catch (e) {
      setPushMsg({ tone: "err", text: e?.message || "Reset failed." });
    } finally {
      setPushBusy(false);
    }
  };

  const sendDigestNow = async () => {
    setDigestBusy(true);
    setDigestMsg(null);
    try {
      const { data } = await api.post("/admin/send-digest");
      const to = data?.to ? ` to ${data.to}` : "";
      setDigestMsg({
        tone: "ok",
        text: `Digest sent${to}. Check inbox in ~30 seconds (also Spam).`,
      });
    } catch (e) {
      setDigestMsg({
        tone: "err",
        text: e?.response?.data?.detail || e?.message || "Could not send digest.",
      });
    } finally {
      setDigestBusy(false);
    }
  };

  const runDiag = async () => {
    setPushBusy(true);
    setPushMsg(null);
    setDiag(null);
    try {
      // Ensure subscription exists so we have a client_id to diagnose
      const { client_id } = await subscribeToPush([]);
      const { data } = await api.get(`/push/admin-diag/${client_id}`);
      setDiag(data);
    } catch (e) {
      setPushMsg({
        tone: "err",
        text: e?.response?.data?.detail || e?.message || "Diagnostic failed.",
      });
    } finally {
      setPushBusy(false);
    }
  };

  return (
    <div className="bg-tac-surface/40 border border-tac-border rounded-sm p-5 space-y-4" data-testid="admin-alerts-card">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-tac-primary flex items-center gap-1.5 mb-1">
          <BellRinging size={12} weight="fill" /> Statewide Admin Alerts
        </div>
        <p className="text-[14px] text-tac-dim leading-relaxed">
          Real-time push for every new wildfire across all 13 WA counties + a daily
          email digest. Smoke checks, medicals, and prescribed burns are filtered out
          to keep the noise down.
        </p>
      </div>

      <div className="grid sm:grid-cols-2 gap-3">
        <div className="border border-tac-border bg-tac-bg/60 rounded-sm p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <BellRinging size={12} weight="duotone" className="text-tac-primary" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-tac-text">
              Real-time push
            </span>
          </div>
          <p className="text-[13px] text-tac-dim leading-snug mb-2">
            Phone, tablet, or desktop buzzes the moment a new wildfire hits any
            county dispatch CAD.
          </p>
          <button
            data-testid="admin-enable-push"
            onClick={enableAdminPush}
            disabled={pushBusy || !supported}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider bg-tac-primary hover:bg-tac-primary/90 disabled:opacity-40 disabled:cursor-not-allowed text-white rounded-sm transition-colors"
          >
            <BellRinging size={11} weight="fill" />
            {pushBusy ? "Enabling…" : pushEnabled ? "Re-sync admin push" : "Enable on this device"}
          </button>
          <button
            data-testid="admin-test-push"
            onClick={sendAdminTestPush}
            disabled={pushBusy || !supported}
            className="w-full mt-1.5 inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary disabled:opacity-40 disabled:cursor-not-allowed text-tac-text rounded-sm transition-colors"
            title="Sends a simulated wildfire alert to this device"
          >
            <BellRinging size={11} weight="bold" />
            {pushBusy ? "Sending…" : "Send test alert to this device"}
          </button>
          <button
            data-testid="admin-reset-push"
            onClick={resetPush}
            disabled={pushBusy || !supported}
            className="w-full mt-1.5 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-transparent hover:border-tac-warning hover:text-tac-warning disabled:opacity-40 disabled:cursor-not-allowed text-tac-dim rounded-sm transition-colors"
            title="Use if push gets stuck or test pushes keep failing — wipes both browser + server records so you can subscribe fresh"
          >
            Reset push subscription
          </button>
          <button
            data-testid="admin-diag-push"
            onClick={runDiag}
            disabled={pushBusy || !supported}
            className="w-full mt-1.5 inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-transparent hover:border-tac-primary hover:text-tac-primary disabled:opacity-40 disabled:cursor-not-allowed text-tac-faint rounded-sm transition-colors"
            title="Show your subscription's gateway and reachability for debugging"
          >
            Run push diagnostics
          </button>
          {diag && (
            <div
              data-testid="push-diag"
              className="mt-2 p-3 border border-tac-border bg-tac-bg/60 rounded-sm text-[11px] font-mono-tac space-y-1 leading-relaxed"
            >
              {!diag.found ? (
                <div className="text-tac-warning">No subscription found on the server. Click Enable first.</div>
              ) : (
                <>
                  <div>
                    <span className="text-tac-faint">Gateway:</span>{" "}
                    <span className="text-tac-text">{diag.gateway}</span>
                  </div>
                  <div>
                    <span className="text-tac-faint">Host:</span>{" "}
                    <span className="text-tac-text break-all">{diag.endpoint_host}</span>
                  </div>
                  <div>
                    <span className="text-tac-faint">Reachable from server:</span>{" "}
                    {diag.reachability?.ok ? (
                      <span className="text-emerald-400">
                        ✓ HTTP {diag.reachability.http_status} in {diag.reachability.round_trip_ms}ms
                      </span>
                    ) : (
                      <span className="text-tac-danger">
                        ✗ {diag.reachability?.error || "unreachable"}
                      </span>
                    )}
                  </div>
                  <div>
                    <span className="text-tac-faint">Admin mode:</span>{" "}
                    <span className="text-tac-text">{diag.admin_mode ? "yes" : "no"}</span>
                    {" · "}
                    <span className="text-tac-faint">VAPID email set:</span>{" "}
                    <span className={diag.vapid_email_set ? "text-emerald-400" : "text-tac-warning"}>
                      {diag.vapid_email_set ? "yes" : "default (placeholder)"}
                    </span>
                  </div>
                  {diag.updated_at && (
                    <div>
                      <span className="text-tac-faint">Last updated:</span>{" "}
                      <span className="text-tac-text">{new Date(diag.updated_at).toLocaleString()}</span>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
          {!supported && (
            <div className="text-[12px] text-tac-warning mt-1.5">
              Push not supported in this browser.
            </div>
          )}
          {pushMsg && (
            <div
              data-testid="admin-push-msg"
              className={`text-[12px] mt-1.5 flex items-start gap-1 ${
                pushMsg.tone === "ok" ? "text-tac-safe" : "text-tac-danger"
              }`}
            >
              {pushMsg.tone === "ok" ? <Check size={11} weight="bold" className="mt-0.5 shrink-0" /> : <X size={11} weight="bold" className="mt-0.5 shrink-0" />}
              <span>{pushMsg.text}</span>
            </div>
          )}
        </div>

        <div className="border border-tac-border bg-tac-bg/60 rounded-sm p-3">
          <div className="flex items-center gap-1.5 mb-1.5">
            <EnvelopeSimple size={12} weight="duotone" className="text-tac-primary" />
            <span className="text-[11px] font-bold uppercase tracking-wider text-tac-text">
              Daily email digest
            </span>
          </div>
          <p className="text-[13px] text-tac-dim leading-snug mb-2">
            24-hour statewide overview emailed to admin once per day. Active fires,
            new wildfires, VIIRS hotspots, NWS alerts.
          </p>
          <button
            data-testid="admin-send-digest"
            onClick={sendDigestNow}
            disabled={digestBusy}
            className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 text-[10px] font-bold uppercase tracking-wider border border-tac-border bg-tac-surface hover:bg-tac-surface2 hover:border-tac-primary text-tac-text rounded-sm transition-colors"
          >
            <EnvelopeSimple size={11} weight="bold" />
            {digestBusy ? "Sending…" : "Send digest now"}
          </button>
          {digestMsg && (
            <div
              data-testid="admin-digest-msg"
              className={`text-[12px] mt-1.5 flex items-start gap-1 ${
                digestMsg.tone === "ok" ? "text-tac-safe" : "text-tac-danger"
              }`}
            >
              {digestMsg.tone === "ok" ? <Check size={11} weight="bold" className="mt-0.5 shrink-0" /> : <X size={11} weight="bold" className="mt-0.5 shrink-0" />}
              <span>{digestMsg.text}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}



function ApplicationRow({ label, value, multiline }) {
  return (
    <div className={multiline ? "" : "flex gap-2"}>
      <span className="text-[10px] uppercase tracking-wider font-bold text-tac-faint shrink-0">{label}:</span>
      <span className={`text-tac-text ${multiline ? "block mt-0.5 whitespace-pre-line" : ""}`}>{value}</span>
    </div>
  );
}

function FilterTab({ active, onClick, icon, label, count, testid }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`flex items-center gap-1.5 px-3 py-2 text-[11px] font-bold uppercase tracking-wider rounded-sm border transition-colors ${
        active
          ? "bg-tac-surface2 border-tac-primary text-tac-primary"
          : "bg-transparent border-tac-border text-tac-dim hover:text-tac-text hover:bg-tac-surface"
      }`}
    >
      {icon} {label}
      {count !== null && count !== undefined && (
        <span className={`ml-1 text-[15px] font-mono-tac tabular ${active ? "text-tac-primary" : "text-tac-faint"}`}>· {count}</span>
      )}
    </button>
  );
}
